import { AnalyticsEvents } from '@mondaygpt/analytics-events'
import { useState, useEffect, useRef } from 'react'
import { Outlet, useLocation } from 'react-router-dom'
import { Menu } from 'lucide-react'
import { useIsMobile } from 'hooks/use-mobile'
import { useAnalytics } from 'hooks'
import { intercom } from 'services/intercom/intercom.service'
import { fetchBusinessBilling } from 'modules/billing'
import { fetchBrandKit } from '../api/brand-kit-api'
import { useBrandKitStore } from '../store/brand-kit-store'
import {
  findMyBusinesses,
  selectCurrentBusiness,
  selectCurrentBusinessMissing,
} from 'modules/business/slices/business-entities.slice'
import { selectBusinessId } from 'modules/authentication'
import { useAppDispatch, useAppSelector } from 'modules/core'
import { PendingInvitationsBanner } from '../components/PendingInvitationsBanner'
import { CreditExhaustedBanner } from 'modules/billing'
import { AppSidebar } from './AppSidebar'
import { pathToPageName } from '../surfaces'
import { AssistantDock } from '../components/AssistantDock'

export function AppLayout() {
  const isMobile = useIsMobile()
  const location = useLocation()
  const isDetailPage = isMobile && location.pathname !== '/'
  const [sidebarOpen, setSidebarOpen] = useState(false)
  // Support replies badge the Get Support row inside the drawer; on mobile the
  // drawer is usually closed, so the hamburger carries the same unread count.
  const [unreadCount, setUnreadCount] = useState(() => intercom.getUnreadCount())
  useEffect(() => intercom.onUnreadCountChange(setUnreadCount), [])
  const { track, registerSuperProperties, group } = useAnalytics()
  const prevPathRef = useRef(location.pathname)
  const dispatch = useAppDispatch()
  const currentBusiness = useAppSelector(selectCurrentBusiness)
  const businessIdFromToken = useAppSelector(selectBusinessId)
  const currentBusinessMissing = useAppSelector(selectCurrentBusinessMissing)

  // Register business as super-properties (attached to every event) and as the
  // PostHog group, so client events join business-level analytics like server events
  useEffect(() => {
    if (currentBusiness) {
      registerSuperProperties({
        business_id: currentBusiness.id,
        business_name: currentBusiness.name,
      })
      group('business', currentBusiness.id)
    }
  }, [currentBusiness?.id, currentBusiness?.name, registerSuperProperties, group])

  // Fetch credit balance when business becomes available
  useEffect(() => {
    if (currentBusiness?.id) {
      dispatch(fetchBusinessBilling(currentBusiness.id))
    }
  }, [currentBusiness?.id, dispatch])

  // Refetch brand kit on business change. Reset FIRST so the stale (previous-
  // business) kit can't flash before the API returns — switchBusinessAction
  // already resets on switch, but this covers logout/login cycles where the
  // persisted Zustand store still holds the previous user's data.
  const previousBusinessIdRef = useRef<string | undefined>(undefined)
  useEffect(() => {
    const id = currentBusiness?.id
    if (!id) return
    if (previousBusinessIdRef.current && previousBusinessIdRef.current !== id) {
      useBrandKitStore.getState().resetBrandKit()
    }
    previousBusinessIdRef.current = id
    fetchBrandKit()
      .then(data => useBrandKitStore.getState().setBrandKitFromApi(data))
      .catch(err => console.error('Failed to fetch brand kit:', err))
  }, [currentBusiness?.id])

  // Recovery path: JWT references a business not in the entity list (deleted
  // in another tab or removed by an admin). Refetch the list — if it still
  // doesn't show up, AppLayout renders nothing for the chrome and any
  // business-scoped API call surfaces its own error.
  useEffect(() => {
    if (currentBusinessMissing && businessIdFromToken) {
      console.warn(
        `[AppLayout] Token businessId ${businessIdFromToken} not in entity list — refetching`
      )
      dispatch(findMyBusinesses())
    }
  }, [currentBusinessMissing, businessIdFromToken, dispatch])

  // Track page views on route change
  useEffect(() => {
    const pageName = pathToPageName(location.pathname)
    if (pageName) {
      track(AnalyticsEvents.Application.PAGE_VIEWED, {
        page_name: pageName,
        platform: isMobile ? 'mobile' : 'desktop',
      })
    }
    prevPathRef.current = location.pathname
  }, [location.pathname, isMobile, track])

  // Lock body scroll when mobile sidebar is open
  useEffect(() => {
    if (sidebarOpen && isMobile) {
      document.body.style.overflow = 'hidden'
    } else {
      document.body.style.overflow = ''
    }
    return () => {
      document.body.style.overflow = ''
    }
  }, [sidebarOpen, isMobile])

  // Allow child pages to open the sidebar via custom event
  useEffect(() => {
    const handler = () => setSidebarOpen(true)
    window.addEventListener('open-sidebar', handler)
    return () => window.removeEventListener('open-sidebar', handler)
  }, [])

  // The OAuth popup's picker/confirm step runs inside the popup window, where the app chrome doesn't belong.
  if (new URLSearchParams(location.search).get('fromOAuth') === '1') {
    return (
      <div className="taka-dark h-dvh w-full overflow-y-auto bg-bg text-paper">
        <Outlet />
      </div>
    )
  }

  return (
    // T3 dark canvas. The `taka-dark` wrapper class scopes the pattern
    // primitives (.btn, .card, .pill, etc.) to this subtree, keeping them
    // off legacy surfaces. `bg-bg` flips with `[data-theme]`.
    <div className="taka-dark flex h-dvh w-full overflow-hidden bg-bg text-paper">
      {/* Mobile sidebar drawer */}
      {isMobile && (
        <>
          {sidebarOpen && (
            <div
              className="fixed inset-0 z-40 bg-black/70 backdrop-blur-sm"
              onClick={() => {
                setSidebarOpen(false)
                track(AnalyticsEvents.Application.MOBILE_SIDEBAR_TOGGLED, { action: 'closed' })
              }}
            />
          )}
          <div
            className={`fixed inset-y-0 left-0 z-50 transform border-r border-line bg-bg-2 transition-transform duration-300 ease-in-out ${
              sidebarOpen ? 'translate-x-0' : 'invisible -translate-x-full'
            }`}
          >
            <AppSidebar isMobile onClose={() => setSidebarOpen(false)} />
          </div>
        </>
      )}

      {/* Desktop sidebar */}
      {!isMobile && (
        <div className="flex-shrink-0 border-r border-line">
          <AppSidebar />
        </div>
      )}

      {/* Main content area */}
      <div className="flex min-w-0 flex-1 flex-col">
        {/* Mobile header — hidden on detail pages (native push nav pattern) */}
        {isMobile && !isDetailPage && (
          <div className="flex items-center border-b border-line bg-bg-2 px-4 py-3">
            <button
              onClick={() => {
                setSidebarOpen(true)
                track(AnalyticsEvents.Application.MOBILE_SIDEBAR_TOGGLED, { action: 'opened' })
              }}
              // -m compensates the fatter padding so the visual position is
              // unchanged while the tap target grows to ~42px (was ~30px).
              className="-m-1.5 rounded-[8px] p-2.5 text-paper-dim transition-colors hover:bg-bg-3 hover:text-paper"
              aria-label="Open menu"
            >
              <span className="relative inline-flex">
                <Menu size={22} />
                {unreadCount > 0 && (
                  <span className="absolute -top-1 -right-1 flex h-3 min-w-[12px] items-center justify-center rounded-full bg-[color:var(--warning)] px-0.5 text-[8px] font-bold text-[color:var(--ink-on-acid)]">
                    {unreadCount > 99 ? '99+' : unreadCount}
                  </span>
                )}
              </span>
            </button>
            <span className="ml-2 text-[15px] font-semibold tracking-[-0.01em] text-paper">
              Taka
            </span>
          </div>
        )}

        {/* Pending invitations banner */}
        <PendingInvitationsBanner />

        {/* Out-of-credits banner — only renders when out of credits */}
        <CreditExhaustedBanner />

        {/* Route content */}
        <div className="flex-1 overflow-y-auto">
          <Outlet />
        </div>

        {/* Global assistant summon — same colleague on every screen (rooms
            with a resident chat are excluded inside the dock). */}
        <AssistantDock />
      </div>
    </div>
  )
}
